import { isValidElement, lazy, type ComponentType, type LazyExoticComponent } from 'react'
import type { RouteObject } from 'react-router-dom'

type Importer = () => Promise<unknown>
// الحدّ نفسه الذي تستعمله React.lazy — أي مكوّن أياً كانت خصائصه
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>
type PreloadableComponent = LazyExoticComponent<AnyComponent> & { preload?: Importer }

/**
 * صفحة كسولة تُحمَّل عند الحاجة — مع القدرة على تحميلها مسبقاً:
 * الشاشات تُقسَّم إلى ملفات صغيرة تُنزَّل عند أول فتح، وكل تنزيل يكلّف ذهاباً وإياباً للخادم (~١٢٠ مللي
 * ثانية من العراق). التحميل المسبق عند تمرير الفأرة على عنصر القائمة أو بوقت الفراغ يجعل الفتح فورياً،
 * فلا يبقى إلا طلب البيانات.
 */
const loaders = new Map<string, Importer>()
const loaded = new Set<string>()

export function lazyPage<T extends AnyComponent>(importer: () => Promise<{ default: T }>) {
  const comp = lazy(importer) as LazyExoticComponent<T> & { preload?: Importer }
  comp.preload = importer
  return comp
}

/** المكوّن الكسول داخل عنصر المسار — العنصر يكون <Suspense>{<Page/>}</Suspense> أو <Page/> مباشرة */
function preloaderOf(element: unknown): Importer | null {
  if (!isValidElement(element)) return null
  const type = element.type as PreloadableComponent
  if (typeof type.preload === 'function') return type.preload
  const inner = (element.props as { children?: unknown }).children
  return isValidElement(inner) ? preloaderOf(inner) : null
}

/** يربط كل مسارٍ ثابت (بلا معاملات) بمُحمِّل صفحته — يُستدعى مرة بعد بناء جدول المسارات */
export function registerLazyRoutes(routes: RouteObject[]) {
  for (const r of routes) {
    if (r.path && !r.path.includes(':') && !r.path.includes('*')) {
      const p = preloaderOf(r.element)
      if (p) loaders.set(r.path, p)
    }
    if (r.children) registerLazyRoutes(r.children)
  }
}

/** يحمّل ملف الصفحة مسبقاً إن عُرف مسارها — مرة واحدة، وبصمت عند الفشل */
export function prefetchRoute(path: string): boolean {
  const importer = loaders.get(path)
  if (!importer || loaded.has(path)) return false
  loaded.add(path)
  importer().catch(() => loaded.delete(path))
  return true
}

/** المسارات المسجَّلة — للتحميل المسبق بوقت الفراغ */
export const registeredRoutes = () => [...loaders.keys()]

/** للاختبارات فقط */
export function _resetPrefetch() {
  loaders.clear()
  loaded.clear()
}
